import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, normalizeStyle as _normalizeStyle, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["name", "value"]
const _hoisted_2 = ["data-qa"]
const _hoisted_3 = { class: "option-label" }
const _hoisted_4 = {
  key: 0,
  class: "loader-alt"
}
const _hoisted_5 = {
  key: 1,
  class: "error-message"
}
const _hoisted_6 = ["data-qa"]
const _hoisted_7 = ["data-id", "onClick", "data-qa"]
const _hoisted_8 = { class: "dropdown-label" }
const _hoisted_9 = { key: 0 }

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

interface Option {
  label: string;
  value: string | number;
  text?: string;
  hint?: string;
  id?: string;
}

interface Props {
  name: string;
  modelValue: string | number | null | (string | number)[];
  options: Option[] | [];
  placeholder?: string;
  placeholderEmpty?: string;
  selectType?: 'radio' | 'checkbox' | null;
  disabled?: boolean;
  hasOverflow?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  autoSelectFirst?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VitaSelect',
  props: {
    name: {},
    modelValue: {},
    options: {},
    placeholder: {},
    placeholderEmpty: {},
    selectType: {},
    disabled: { type: Boolean },
    hasOverflow: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
    autoSelectFirst: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "open", "retry"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);
const isEmptyOptions = computed(() => props.options.length === 0);
const selectDisabled = computed(() => props.disabled || isEmptyOptions.value);
const scrollOptions = { wheelPropagation: false, suppressScrollX: true, minScrollbarLength: 50 };

const rootRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const dropdownStyles = ref<{ top: string; left: string; width: string }>({
  top: '0px',
  left: '0px',
  width: '0px',
});

const updateDropdownPosition = () => {
  if (!rootRef.value || !isOpen.value) return;

  const rect = rootRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  const dropdownEl = dropdownRef.value;
  const SPACING = 6;
  const DEFAULT_HEIGHT = 220;
  const dropdownHeight = dropdownEl?.offsetHeight || DEFAULT_HEIGHT;

  const openDown = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

  const top = openDown
    ? rect.bottom + window.scrollY + SPACING
    : rect.top + window.scrollY - dropdownHeight - SPACING;

  dropdownStyles.value = {
    top: `${top}px`,
    left: `${rect.left + window.scrollX}px`,
    width: `${rect.width}px`,
  };
};

const onClickOutside = (event: MouseEvent) => {
  if (!isOpen.value) return;

  const target = event.target as Node | null;

  if (!target) return;
  if (rootRef.value && rootRef.value.contains(target)) return;
  if (dropdownRef.value && dropdownRef.value.contains(target)) return;

  closeSelectDrop();
};

const addClickOutsideListener = () => {
  document.addEventListener('mousedown', onClickOutside);
};

const removeClickOutsideListener = () => {
  document.removeEventListener('mousedown', onClickOutside);
};

const addPositionListeners = () => {
  window.addEventListener('scroll', updateDropdownPosition, true);
  window.addEventListener('resize', updateDropdownPosition);
};

const removePositionListeners = () => {
  window.removeEventListener('scroll', updateDropdownPosition, true);
  window.removeEventListener('resize', updateDropdownPosition);
};

// Получаем отображаемый label выбранного значения (или значений для режима чекбоксов)
const selectedLabel = computed(() => {
  if (Array.isArray(props.modelValue)) {
    if (props.modelValue.length === 0) return null;

    if (props.modelValue.length === 1) {
      const firstValue = props.modelValue[0];
      return props.options.find((o) => o.value === firstValue)?.label;
    }

    return `${props.options.find((o) => o.value === (props.modelValue as (string | number)[])[0])?.label} +${props.modelValue.length - 1}`;
  }
  return props.options.find((o) => o.value === props.modelValue)?.label;
});

// Текст в поле: для пустого списка — placeholderEmpty, иначе выбранное значение или placeholder
const fieldLabel = computed(() => {
  if (isEmptyOptions.value && props.placeholderEmpty) return props.placeholderEmpty;
  return selectedLabel.value ?? props.placeholder ?? 'Выберите значение';
});

const isSelected = computed(() => (value: string | number) => {
  if (Array.isArray(props.modelValue)) {
    return props.modelValue.includes(value);
  }
  return props.modelValue === value;
});

// Обрабатываем нажатие на опцию (radio/checkbox режим)
const select = (option: Option) => {
  if (props.selectType === 'checkbox') {
    const currentValue = Array.isArray(props.modelValue) ? props.modelValue : [];
    const index = currentValue.indexOf(option.value);

    if (index > -1) {
      const newValue = currentValue.filter((v) => v !== option.value);
      emit('update:modelValue', newValue);
    } else {
      emit('update:modelValue', [...currentValue, option.value]);
    }
  } else {
    emit('update:modelValue', option.value);
    isOpen.value = false;
  }
};

// Следим за изменением списка: при включённом autoSelectFirst первая опция выбирается автоматически
watch(
  () => props.options,
  (newOptions) => {
    if (!props.autoSelectFirst || newOptions.length === 0) return;

    const firstValue = newOptions[0].value;
    if (props.selectType === 'checkbox') {
      if (Array.isArray(props.modelValue) && props.modelValue.length === 0) {
        emit('update:modelValue', [firstValue]);
      }
    } else if (!props.modelValue) {
      emit('update:modelValue', firstValue);
    }
  },
  { immediate: true },
);

const toggleOpen = () => {
  if (selectDisabled.value) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    emit('open');
    nextTick(() => {
      updateDropdownPosition();
      addPositionListeners();
      addClickOutsideListener();
    });
  } else {
    removePositionListeners();
    removeClickOutsideListener();
  }
};

const closeSelectDrop = () => {
  isOpen.value = false;
  removePositionListeners();
  removeClickOutsideListener();
};

watch(
  () => [props.options.length, props.isLoading],
  () => {
    if (isOpen.value) {
      nextTick(() => {
        updateDropdownPosition();
      });
    }
  },
);

onMounted(() => {
  if (isOpen.value) {
    nextTick(() => {
      updateDropdownPosition();
      addPositionListeners();
      addClickOutsideListener();
    });
  }
});

onBeforeUnmount(() => {
  removePositionListeners();
  removeClickOutsideListener();
});

return (_ctx: any,_cache: any) => {
  const _component_PerfectScrollbar = _resolveComponent("PerfectScrollbar")!

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: _normalizeClass(["custom-select", [__props.selectType, { 'is-open': isOpen.value, 'is-disabled': selectDisabled.value, 'is-loading': __props.isLoading }]]),
    tabindex: "0"
  }, [
    _createElementVNode("input", {
      type: "text",
      name: __props.name,
      value: __props.modelValue,
      autocomplete: "off"
    }, null, 8, _hoisted_1),
    _createElementVNode("div", {
      class: _normalizeClass(["field", { 'not-selected': !selectedLabel.value }]),
      onClick: toggleOpen,
      "data-qa": `select-${__props.name}-field-toggle`
    }, [
      _createElementVNode("div", _hoisted_3, _toDisplayString(fieldLabel.value), 1),
      _cache[1] || (_cache[1] = _createElementVNode("span", { class: "arrow" }, null, -1))
    ], 10, _hoisted_2),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "dropdownRef",
            ref: dropdownRef,
            class: _normalizeClass(["dropdown", { 'has-overflow': __props.hasOverflow }]),
            style: _normalizeStyle(dropdownStyles.value)
          }, [
            _renderSlot(_ctx.$slots, "loader"),
            (__props.isLoading)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[2] || (_cache[2] = [
                  _createElementVNode("span", { class: "loader-alt-spinner" }, null, -1)
                ]))]))
              : _createCommentVNode("", true),
            (!__props.isLoading && __props.hasError)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _cache[4] || (_cache[4] = _createElementVNode("div", null, "Ошибка загрузки данных", -1)),
                  _createElementVNode("button", {
                    class: "btn-refresh",
                    type: "button",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('retry'))),
                    "data-qa": `select-${__props.name}-button-retry`
                  }, [...(_cache[3] || (_cache[3] = [
                    _createElementVNode("svg", {
                      width: "16",
                      height: "16",
                      viewBox: "0 0 16 16",
                      fill: "#30A8FF",
                      xmlns: "http://www.w3.org/2000/svg"
                    }, [
                      _createElementVNode("path", { d: "M15.3331 7.33333C15.1563 7.33333 14.9868 7.40357 14.8617 7.5286C14.7367 7.65362 14.6665 7.82319 14.6665 8C14.6719 9.55018 14.1388 11.0541 13.1582 12.2548C12.1776 13.4554 10.8104 14.2782 9.29041 14.5825C7.77038 14.8869 6.19185 14.6538 4.82461 13.9232C3.45737 13.1927 2.38631 12.0099 1.7945 10.5771C1.2027 9.14433 1.12689 7.55049 1.58003 6.06801C2.03317 4.58552 2.98713 3.30644 4.27885 2.44938C5.57057 1.59232 7.11986 1.2105 8.66191 1.36918C10.204 1.52786 11.643 2.21718 12.7331 3.31933C12.7112 3.32531 12.689 3.32999 12.6665 3.33333H10.6665C10.4897 3.33333 10.3201 3.40357 10.1951 3.5286C10.0701 3.65362 9.99982 3.82319 9.99982 4C9.99982 4.17681 10.0701 4.34638 10.1951 4.4714C10.3201 4.59643 10.4897 4.66667 10.6665 4.66667H12.6665C13.1969 4.66667 13.7056 4.45595 14.0807 4.08088C14.4558 3.70581 14.6665 3.1971 14.6665 2.66667V0.666667C14.6665 0.489856 14.5962 0.320286 14.4712 0.195262C14.3462 0.0702379 14.1766 0 13.9998 0C13.823 0 13.6534 0.0702379 13.5284 0.195262C13.4034 0.320286 13.3331 0.489856 13.3331 0.666667V2.04333C11.9682 0.82302 10.2258 0.108111 8.39705 0.0180684C6.56834 -0.0719742 4.76411 0.468305 3.28587 1.54862C1.80762 2.62894 0.744905 4.18386 0.275299 5.95354C-0.194308 7.72322 -0.0423553 9.60046 0.705764 11.2716C1.45388 12.9427 2.75285 14.3064 4.3856 15.1349C6.01836 15.9634 7.88599 16.2065 9.67641 15.8235C11.4668 15.4405 13.0716 14.4547 14.2225 13.0307C15.3734 11.6067 16.0008 9.83093 15.9998 8C15.9998 7.82319 15.9296 7.65362 15.8046 7.5286C15.6795 7.40357 15.51 7.33333 15.3331 7.33333Z" })
                    ], -1),
                    _createElementVNode("span", null, "Обновить", -1)
                  ]))], 8, _hoisted_6)
                ]))
              : _createCommentVNode("", true),
            (!__props.isLoading && __props.options.length > 0 && !__props.hasError)
              ? (_openBlock(), _createBlock(_component_PerfectScrollbar, {
                  key: 2,
                  class: "dropdown-scroll",
                  options: scrollOptions
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("ul", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.options, (option) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: option.id,
                          "data-id": option.id,
                          class: _normalizeClass({ active: isSelected.value(option.value) }),
                          onClick: ($event: any) => (select(option)),
                          "data-qa": `select-${__props.name}-option-choose`
                        }, [
                          _createElementVNode("div", _hoisted_8, _toDisplayString(option.label), 1),
                          (option.text)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                _createElementVNode("span", null, _toDisplayString(option.text), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ], 10, _hoisted_7))
                      }), 128))
                    ])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ], 6))
        : _createCommentVNode("", true)
    ]))
  ], 2))
}
}

})